import { PopperMenu } from "@metacrm/metacrm-material-ui/dist/PopperMenu";
import { PreviewFile } from "@metacrm/metacrm-material-ui/dist/PreviewFile";
import { IconTooltip } from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import IconIcDiscord2 from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcDiscord2";
import IconIcTelegram from "@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTelegram";
import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import axios from "axios";
import { apiUrl } from "features/configure";
import {
  formatDiscordUsername,
  getShortAddress,
  isVersionMoreThan,
  formatDate,
} from "features/helpers/utils";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import {
  selectWidgetModule,
  selectTranslationEnabled,
  selectTranslationTargetLanguage,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import Linkify from "linkify-react";
import _, { get } from "lodash";
import { useRef, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledAdminMessage,
  StyledAdminMessageTime,
  StyledMessageRow,
  StyledMsgAddress,
  StyledThumbIcon,
  StyledUserMessage,
  StyledUserMessageTime,
  StyledLanguageSelector,
  StyledTranslationContainer,
} from "../Chat.styles";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useFetchTeamMember } from "../../redux/hooks";
import alertSvg from "assets/img/ic_alert.svg";
import MessageRefine from "./MessageRefine";
import useTranslation, { useLanguageSelector } from "../hooks/useTranslation";

const ChatItem = ({
  data,
  icon,
  index,
  lastOne,
  lastData,
  setOpenImg,
  discordId,
  telegramId,
  selectedTicket,
  entityName,
  onRecallMsg,
  refreshPastMessages,
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const isAiResponse = get(data, "team.name", "") === "AI Agent";

  const [showTranslation, setShowTranslation] = useState(false);

  const widgetModule = useSelector(selectWidgetModule);
  const translationEnabled = useSelector(selectTranslationEnabled);
  const translationTargetLanguage = useSelector(
    selectTranslationTargetLanguage
  );

  const {
    fromLanguage,
    toLanguage,
    fromLangMenuOpen,
    toLangMenuOpen,
    fromLangRef,
    toLangRef,
    setFromLangMenuOpen,
    setToLangMenuOpen,
    getLanguageLabel,
    createLanguageMenuItems,
    handleFromLanguageChange,
    handleToLanguageChange,
  } = useLanguageSelector(
    "",
    data.messageTranslated?.targetLanguage || translationTargetLanguage
  );

  const {
    translateMessage,
    getTranslation,
    isTranslationLoading,
    clearTranslations,
  } = useTranslation();

  useEffect(() => {
    return () => {
      // 組件卸載時清理翻譯狀態和取消請求
      clearTranslations();
    };
  }, [clearTranslations]);

  useEffect(() => {
    if (data.messageTranslated?.translatedText && translationEnabled) {
      setShowTranslation(true);
    }
  }, [data.messageTranslated, translationEnabled]);

  const options = {
    target: (href) => {
      const { hostname } = window.location;
      if (hostname && href.includes(hostname)) {
        return "";
      }
      return "_blank";
    },
    attributes: {
      rel: "noreferrer noopener",
    },
  };

  const dispatch = useDispatch();
  const { memberList } = useFetchTeamMember();
  const address = _.get(selectedTicket, "userWallet.address", "");
  const status = _.get(data, "status", "");
  const isRecalled = _.get(data, "isRecalled", false);
  const team = _.get(data, "team", null);

  const [openMore, setOpenMore] = useState(false);
  const anchorRef = useRef(null);
  const confirm = useConfirm();

  const widgetConnected = _.get(widgetModule, "connected", false);
  const widgetConnecting = _.get(widgetModule, "connecting", false);

  const widgetVersion = _.get(widgetModule, "version", "");

  const handleTranslateClick = () => {
    setShowTranslation(!showTranslation);
    // 如果要顯示翻譯且有訊息，但是沒有預設的翻譯內容時才呼叫 API
    if (
      !showTranslation &&
      data.message &&
      !data.messageTranslated?.translatedText
    ) {
      handleTranslate(fromLanguage, toLanguage);
    }
  };

  const handleTranslate = async (from, to) => {
    if (!data.message || !data._id) return;
    await translateMessage(data._id, data.message, from, to);
  };

  const onLanguageChange = (from, to) => {
    if (showTranslation) {
      handleTranslate(from, to);
    }
  };

  const fromLanguageChangeHandler = handleFromLanguageChange(onLanguageChange);
  const toLanguageChangeHandler = handleToLanguageChange(onLanguageChange);

  const renderTime = () => {
    if (!data.created) return <div />;
    return formatDate(data.created, { time: true });
  };

  const renderStatusDisplay = () => {
    if (status == "sending") return "Sending...";
    if (status == "failed")
      return (
        <i
          className="meta-crm-icon-ic_info_fill font-size-16"
          style={{ color: "#FC5555" }}
        />
      );
    return renderTime();
  };

  const renderTranslateIcon = () => {
    if (!data.message || isRecalled || !translationEnabled) return null;

    return (
      <>
        <IconTooltip
          icon={
            <IconButton
              size="small"
              onClick={handleTranslateClick}
              style={{
                padding: "2px",
                marginLeft: "4px",
                color: showTranslation && theme.customColors.purple[500],
              }}
            >
              <i className="meta-crm-icon-ic_translate font-size-16" />
            </IconButton>
          }
          content={formatMessage({ id: "global.btn.translate" })}
        />
        {/* 語言選擇器 */}
        {showTranslation && (
          <Box display="flex" gap={1} alignItems="center">
            {formatMessage({ id: "global.commonWord.from" })}

            <Box
              ref={fromLangRef}
              onClick={() => setFromLangMenuOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "12px",
                color: theme.customColors.purple[500],
              }}
            >
              {getLanguageLabel(fromLanguage)}
              <i className="meta-crm-icon-ic_arrow-down font-size-16" />
            </Box>

            <PopperMenu
              open={fromLangMenuOpen}
              onClose={() => setFromLangMenuOpen(false)}
              anchorEl={fromLangRef.current}
              placement="bottom-end"
              itemList={createLanguageMenuItems(
                true,
                fromLanguageChangeHandler
              )}
            />

            {formatMessage({ id: "global.commonWord.to" })}
            <StyledLanguageSelector
              ref={toLangRef}
              onClick={() => setToLangMenuOpen(true)}
            >
              {getLanguageLabel(toLanguage)}
              <i className="meta-crm-icon-ic_arrow-down font-size-16" />
            </StyledLanguageSelector>

            <PopperMenu
              open={toLangMenuOpen}
              onClose={() => setToLangMenuOpen(false)}
              anchorEl={toLangRef.current}
              placement="bottom-end"
              itemList={createLanguageMenuItems(false, toLanguageChangeHandler)}
            />
          </Box>
        )}
      </>
    );
  };

  const renderTranslationContent = () => {
    if (!showTranslation || !data.message || isRecalled) return null;

    const translationResult = getTranslation(
      data._id,
      fromLanguage,
      toLanguage
    );
    const isLoading = isTranslationLoading(data._id, fromLanguage, toLanguage);

    return (
      <StyledTranslationContainer>
        <Box>
          {isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <img
                src={require("assets/img/import-loading.gif")}
                style={{ width: 16, height: 16 }}
              />
              {formatMessage({ id: "global.status.translating" })}
            </Box>
          ) : translationResult ? (
            // 優先顯示 API 翻譯結果（當用戶改變語言選項時）
            <Linkify options={options}>
              {translationResult.translatedText}
            </Linkify>
          ) : data.messageTranslated?.translatedText ? (
            // 如果沒有 API 翻譯結果，則顯示預設的翻譯內容
            <Linkify options={options}>
              {data.messageTranslated.translatedText}
            </Linkify>
          ) : (
            formatMessage({ id: "global.status.noTranslation" })
          )}
        </Box>
      </StyledTranslationContainer>
    );
  };

  const handleMoreAction = () => {
    setOpenMore(true);
  };

  const handleCloseMoreAction = () => {
    setOpenMore(false);
  };

  const dispatchSnackbar = useDispatchSnackbar();

  const handleRecall = async () => {
    try {
      const { data: resData } = await axios.post(
        `${apiUrl}/api/messages/manage/${entityName}/recall`,
        {
          messageId: _.get(data, "_id"),
          ticketId: _.get(selectedTicket, "_id"),
        }
      );

      onRecallMsg(resData);

      // handleBack();
    } catch (err) {
      dispatchSnackbar({ err });
    }
  };

  const handleRecallConfirm = () => {
    handleCloseMoreAction();
    confirm({
      title: formatMessage({
        id: "popup.recallConfirmation.title",
      }),
      content: formatMessage({
        id: "popup.recallConfirmation.description",
      }),
      confirmText: formatMessage({
        id: "global.btn.confirm",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(() => {
        handleRecall();
      })
      .catch(() => {});
  };

  const itemList = [
    {
      children: (
        <Box color="#FC5555">{formatMessage({ id: "global.btn.recall" })}</Box>
      ),
      func: (e) => {
        handleRecallConfirm(e);
      },
    },
  ];

  const renderMoreBtn = () => {
    return (
      <>
        <IconTooltip
          icon={
            <StyledThumbIcon
              onClick={(event) => handleMoreAction(event)}
              size={16}
              ref={anchorRef}
              width="16px"
              className="meta-crm-icon-ic_moreDots font-size-16"
            />
          }
          content={formatMessage({ id: "general.btn.moreTools" })}
        />

        <PopperMenu
          open={openMore}
          onClose={handleCloseMoreAction}
          anchorEl={anchorRef.current}
          placement="bottom"
          itemList={itemList}
        />
      </>
    );
  };

  const renderMoreBtnCondition = () => {
    if (data?.isSystemMessage) return;
    if (selectedTicket.source === "ticketAPI") return;
    // 不是已經被Recall的訊息 && 不是傳送失敗的訊息 && 不是傳送中的訊息 && 訊息是portal admin回覆的
    if (!isRecalled && status !== "failed" && status !== "sending" && team) {
      // 客戶有安裝widget && widget版本 > 1.1.1 (1.1.2版本才支援recall)
      if (widgetConnected || widgetConnecting) {
        if (isVersionMoreThan(widgetVersion, "1.1.1")) {
          return renderMoreBtn();
        }
      } else {
        // 客戶沒有安裝widget
        return renderMoreBtn();
      }
    }
  };

  const renderAdminChatBox = () => {
    return (
      <div key={index}>
        {getAdminName()}
        <Box mb="18px">
          <StyledMessageRow status={status} isRecalled={isRecalled}>
            <StyledAdminMessage sending={status == "sending"}>
              <Box sx={{ padding: "12px" }}>
                <Linkify options={options}>{data.message}</Linkify>
              </Box>
              <PreviewFile
                files={data.imagesUrl}
                bigImg={true}
                onImgClick={(image) => setOpenImg(image)}
                white
                videoProps={{ width: 375, height: 200 }}
              />
              {renderTranslationContent()}
            </StyledAdminMessage>
          </StyledMessageRow>

          <Box className="item-flex" justifyContent="flex-end" mt="3px">
            {!isRecalled && (
              <MessageRefine
                data={data}
                content={renderMoreBtnCondition()}
                refreshPastMessages={refreshPastMessages}
              />
            )}
            <Box
              color="#7B61FF"
              fontSize={12}
              fontWeight={400}
              mr="3px"
              ml="8px"
            >
              {isRecalled ? (
                <Box className="item-flex">
                  {formatMessage({ id: "chatroom.messageState.recall" })}{" "}
                  {_.get(data, "recallBy.name", "admin")}
                </Box>
              ) : (
                isAiResponse && "AI"
              )}
            </Box>
            <StyledAdminMessageTime>
              {renderStatusDisplay()}
              {renderTranslateIcon()}
            </StyledAdminMessageTime>
          </Box>
        </Box>
      </div>
    );
  };

  const renderUserChatBox = () => {
    return (
      <div key={index}>
        {lastOne !== "user" && (
          <div style={{ margin: "10px 0 7px 0" }}>{getAddress()}</div>
        )}
        <Box mb="18px" ml="20px">
          <StyledMessageRow
            style={{ justifyContent: "flex-start" }}
            isRecalled={isRecalled}
          >
            <StyledUserMessage index={index}>
              {isRecalled ? (
                <Box sx={{ padding: "12px" }}>
                  {formatMessage({
                    id: "chatroom.messageState.recall",
                  })}{" "}
                  user
                </Box>
              ) : (
                <>
                  <Box sx={{ padding: "12px" }}>
                    <Linkify options={options}>{data?.message}</Linkify>
                  </Box>
                  <PreviewFile
                    files={data.imagesUrl}
                    bigImg={true}
                    onImgClick={(image) => setOpenImg(image)}
                    videoProps={{ width: 375, height: 200 }}
                  />
                  {renderTranslationContent()}
                </>
              )}
            </StyledUserMessage>
          </StyledMessageRow>

          <Box mt="3px">
            <StyledUserMessageTime>
              {data.edited && (
                <div>{formatMessage({ id: "global.status.edited" })}</div>
              )}
              {renderTime()}
              {renderTranslateIcon()}
            </StyledUserMessageTime>
          </Box>
        </Box>
      </div>
    );
  };

  const getAdminName = () => {
    const lastAdminName =
      formatDiscordUsername(_.get(lastData, "discordUsername")) ||
      _.get(lastData, "team.name");
    const adminName =
      formatDiscordUsername(_.get(data, "discordUsername")) ||
      _.get(data, "team.name");
    const adminFromDiscord = !!_.get(data, "discordUsername");
    if (lastAdminName == adminName || adminName === "AI Agent") return;

    const adminDiscordUserId = _.get(data, "discordUserId");
    // check discord agent is bind or not
    const adminDiscordUnbound = !memberList.some(
      (member) => member.discordId === adminDiscordUserId
    );

    const unboundTooltip = adminDiscordUnbound && (
      <IconTooltip
        placement="top-start"
        black
        icon={
          // <i
          //   className="meta-crm-icon-ic_alert font-size-64"
          //   style={{ color: "#FFC814" }}
          // />
          <img src={alertSvg} alt="alert" style={{ width: 20, height: 20 }} />
        }
        content={
          <Box fontWeight={500} fontSize={"10px"} margin={"-10px -2px"}>
            {formatMessage({ id: "ticketChat.tooltip.unboundDiscordHandle" })}
          </Box>
        }
      />
    );

    return (
      <div style={{ marginBottom: 5 }} className="endRow">
        {adminFromDiscord ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {unboundTooltip}
            <IconIcDiscord2
              width={20}
              height={20}
              sx={{ filter: adminDiscordUnbound ? "grayscale(100%)" : "none" }}
            />
            <span
              style={{
                color: "#555555",
                fontWeight: 500,
                fontSize: 12,
                textDecorationLine: "underline",
              }}
            >
              {adminName}
            </span>
          </Box>
        ) : (
          <>
            <img
              src={require("assets/img/ic_alpacaBlacBigFace.svg").default}
              style={{ width: 18, height: 18, marginRight: 5 }}
            />
            <span
              style={{
                color: "#555555",
                fontWeight: 500,
                fontSize: 12,
                textDecorationLine: "underline",
              }}
            >
              {adminName}
            </span>
          </>
        )}
      </div>
    );
  };

  const getAddress = () => {
    return (
      <StyledMsgAddress className={"startRow"}>
        {address && (
          <div
            className="startRow"
            style={{
              color: "#555555",
              fontWeight: 500,
            }}
          >
            <img
              src={require(`assets/img/${icon}`)}
              style={{
                width: 20,
                height: 20,
                borderRadius: 50,
                marginRight: 4,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: "#777777",
              }}
            >
              {getShortAddress(_.get(selectedTicket, "userWallet.address", ""))}
            </span>
          </div>
        )}
        {telegramId && (
          <div
            className="startRow"
            style={{
              color: "#555555",
              fontWeight: 500,
              marginLeft: 8,
            }}
          >
            <IconIcTelegram width={20} height={20} className="mgr4" />
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: "#777777",
              }}
            >
              ID: {telegramId}
            </span>
          </div>
        )}
        {discordId && (
          <div
            className="startRow"
            style={{
              color: "#555555",
              fontWeight: 500,
              marginLeft: 8,
            }}
          >
            <IconIcDiscord2 width={20} height={20} className="mgr4" />
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: "#777777",
              }}
            >
              ID: {formatDiscordUsername(discordId)}
            </span>
          </div>
        )}
      </StyledMsgAddress>
    );
  };

  if (data.isAdminResponse) return renderAdminChatBox();
  return renderUserChatBox();
};

export default ChatItem;
